import { create } from 'zustand'

export type AdjustmentKey =
  | 'lightness'
  | 'contrast'
  | 'saturation'
  | 'temperature'
  | 'hue'
  | 'shadows'
  | 'highlights'
  | 'tintRG'
  | 'tintYB'

export type ColorAdjustments = Record<AdjustmentKey, number>

export interface ReferenceSelection {
  x: number
  y: number
  w: number
  h: number
}

export interface WatermarkState {
  text: string
  x: number
  y: number
  color: string
  opacity: number
  size: number
  angle: number
  tile: boolean
  density: number
}

export interface WorkbenchImage {
  source: 'product' | 'upload'
  productId?: string
  uploadedFileId?: string
  categoryId?: number
  categoryCode?: string
  categoryName?: string
  url: string
  thumbnailUrl?: string
  name?: string
  width?: number
  height?: number
  colorGamut?: string
  brightness?: number
}

export const getProductWatermarkName = (image: WorkbenchImage | null): string => {
  if (!image || image.source !== 'product' || !image.name) return ''
  return image.name
}

/**
 * 打样结果上的产品水印文案：
 * - 原图 + 参考图都来自产品库 → `原图型号前三位+参考图型号`（如 `R02+X834859K`）
 * - 仅原图来自产品库（参考图上传/无编码） → 原图型号前三位
 * - 原图非产品库 → 空字符串
 */
export const getResultProductWatermarkName = (
  original: WorkbenchImage | null,
  reference: WorkbenchImage | null,
): string => {
  const originalName = getProductWatermarkName(original)
  if (!originalName) return ''
  const referenceName = getProductWatermarkName(reference)
  if (!referenceName) return originalName.slice(0, 3)
  return `${originalName.slice(0, 3)}+${referenceName}`
}

export interface WorkbenchMeta {
  iteration?: number
  confidence?: number
  deltaE?: { bins: number[]; max: number }
}

const defaultAdjustments: ColorAdjustments = {
  lightness: 0,
  contrast: 1,
  saturation: 1,
  temperature: 0,
  hue: 0,
  shadows: 0,
  highlights: 0,
  tintRG: 0,
  tintYB: 0,
}

const defaultWatermark: WatermarkState = {
  text: '',
  x: -1,
  y: -1,
  color: '#ffffff',
  opacity: 0.5,
  size: 60,
  angle: 0,
  tile: false,
  density: 200,
}

interface WorkbenchState {
  original: WorkbenchImage | null
  reference: WorkbenchImage | null
  previewBlobUrl: string | null
  /** Fingerprint of the merge inputs that produced `previewBlobUrl`, so a
   * remount (e.g. switching back to the mobile result tab) can reuse the cached
   * result instead of re-running the merge. */
  previewSignature: string | null
  wechatPreviewUrl: string | null
  status: 'idle' | 'processing' | 'ready' | 'error'
  progress: number
  errorMessage: string | null
  meta: WorkbenchMeta
  adjustments: ColorAdjustments
  preserveLightness: boolean
  localColorSelection: ReferenceSelection | null
  watermark: WatermarkState
  setOriginal: (image: WorkbenchImage | null) => void
  setReference: (image: WorkbenchImage | null) => void
  setPreview: (url: string | null, signature?: string | null) => void
  setWechatPreviewUrl: (url: string | null) => void
  setMeta: (meta: WorkbenchMeta) => void
  setStatus: (status: WorkbenchState['status']) => void
  setProgress: (progress: number) => void
  setError: (message: string | null) => void
  setAdjustment: (key: AdjustmentKey, value: number) => void
  setPreserveLightness: (value: boolean) => void
  setLocalColorSelection: (selection: ReferenceSelection | null) => void
  setWatermark: (watermark: Partial<WatermarkState>) => void
  applyDatongWatermarkPreset: () => void
  resetAdjustments: () => void
  reset: () => void
}

export const useWorkbenchStore = create<WorkbenchState>((set) => ({
  original: null,
  reference: null,
  previewBlobUrl: null,
  previewSignature: null,
  wechatPreviewUrl: null,
  status: 'idle',
  progress: 0,
  errorMessage: null,
  meta: {},
  adjustments: defaultAdjustments,
  preserveLightness: true,
  localColorSelection: null,
  watermark: defaultWatermark,
  setOriginal: (original) =>
    set((state) => {
      if (state.original?.source === 'upload' && state.original.url.startsWith('blob:')) {
        URL.revokeObjectURL(state.original.url)
      }
      if (state.previewBlobUrl) URL.revokeObjectURL(state.previewBlobUrl)
      return { original, previewBlobUrl: null, previewSignature: null, status: 'idle', errorMessage: null, meta: {} }
    }),
  setReference: (reference) =>
    set((state) => {
      if (state.reference?.source === 'upload' && state.reference.url.startsWith('blob:')) {
        URL.revokeObjectURL(state.reference.url)
      }
      if (state.previewBlobUrl) URL.revokeObjectURL(state.previewBlobUrl)
      return {
        reference,
        localColorSelection: null,
        previewBlobUrl: null,
        previewSignature: null,
        status: 'idle',
        errorMessage: null,
        meta: {},
      }
    }),
  setPreview: (previewBlobUrl, previewSignature = null) =>
    set((state) => {
      if (state.previewBlobUrl && state.previewBlobUrl !== previewBlobUrl) {
        URL.revokeObjectURL(state.previewBlobUrl)
      }
      return {
        previewBlobUrl,
        previewSignature: previewBlobUrl ? previewSignature : null,
        status: previewBlobUrl ? 'ready' : 'idle',
      }
    }),
  setWechatPreviewUrl: (wechatPreviewUrl) =>
    set((state) => {
      if (state.wechatPreviewUrl && state.wechatPreviewUrl !== wechatPreviewUrl && state.wechatPreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(state.wechatPreviewUrl)
      }
      return { wechatPreviewUrl }
    }),
  setMeta: (meta) => set({ meta }),
  setStatus: (status) => set({ status }),
  setProgress: (progress) => set({ progress }),
  setError: (errorMessage) =>
    set({ errorMessage, status: errorMessage ? 'error' : 'idle', progress: 0 }),
  setAdjustment: (key, value) =>
    set((state) => ({ adjustments: { ...state.adjustments, [key]: value } })),
  setPreserveLightness: (preserveLightness) => set({ preserveLightness }),
  setLocalColorSelection: (localColorSelection) => set({ localColorSelection }),
  setWatermark: (watermark) => set((state) => ({ watermark: { ...state.watermark, ...watermark } })),
  applyDatongWatermarkPreset: () =>
    set((state) => ({
      watermark: {
        ...state.watermark,
        text: 'DATONG',
        opacity: 0.35,
        size: 47,
        angle: -45,
        density: 410,
        tile: true,
      },
    })),
  resetAdjustments: () => set({ adjustments: defaultAdjustments, preserveLightness: true }),
  reset: () =>
    set((state) => {
      if (state.original?.source === 'upload' && state.original.url.startsWith('blob:')) {
        URL.revokeObjectURL(state.original.url)
      }
      if (state.reference?.source === 'upload' && state.reference.url.startsWith('blob:')) {
        URL.revokeObjectURL(state.reference.url)
      }
      if (state.previewBlobUrl) URL.revokeObjectURL(state.previewBlobUrl)
      return {
        original: null,
        reference: null,
        previewBlobUrl: null,
        previewSignature: null,
        status: 'idle',
        errorMessage: null,
        meta: {},
        adjustments: defaultAdjustments,
        preserveLightness: true,
        localColorSelection: null,
        watermark: defaultWatermark,
      }
    }),
}))
